import React from "react";
import Grid from "@material-ui/core/Grid";
import IconButton from "@material-ui/core/IconButton";
import TextField from "@material-ui/core/TextField";
import { Close as CloseIcon } from "@material-ui/icons";
import { Button, Labelbox, Modal } from "./index";
import EfilingConsentField from "./EfilingConsentField";
import BusinessEfilingFormFields from "./BusinessEfilingFormFields";

const PAYMENT_METHOD_OPTIONS = [
  { id: "", value: "Select", keepAtTop: true },
  { id: "Direct Deposit", value: "Direct Deposit" },
  // API stores "Check"; UI label is "Paper Check"
  { id: "Check", value: "Paper Check" },
  { id: "Online Payment", value: "Online Payment" },
  { id: "Direct Debit", value: "Direct Debit" },
];

const FILING_STATUS_OPTIONS = [
  { id: "", value: "Select", keepAtTop: true },
  { id: "Efiled", value: "Efiled" },
  { id: "Pending", value: "Pending" },
  { id: "Rejected", value: "Rejected" },
];

const CLIENT_MGR_VERIFIED_OPTIONS = [
  { id: "", value: "Select", keepAtTop: true },
  { id: "Yes", value: "Yes" },
  { id: "No", value: "No" },
];

function ReadonlyField({ label, value, error, errmsg }) {
  return (
    <TextField
      fullWidth
      variant="outlined"
      label={label}
      value={value || ""}
      error={error}
      helperText={error ? errmsg : ""}
      InputProps={{ readOnly: true }}
      margin="dense"
      InputLabelProps={{ shrink: true }}
    />
  );
}

export default function EfilingEditModal({
  open,
  onClose,
  saving,
  canEditFilerComment = false,
  hideStatusAndFilerComment = false,
  form,
  setForm,
  formErrors = {},
  onClearError,
  clientFirst,
  clientLast,
  categoryTab,
  invoiceDropdown = [],
  onInvoiceSelect,
  onSave,
  clientId,
}) {
  const isBusiness = categoryTab === "business";

  const setField = (key, value) => {
    if (onClearError) onClearError(key);
    setForm((s) => ({ ...s, [key]: value }));
  };

  return (
    <Modal
      title={null}
      open={open}
      handleClose={() => !saving && onClose()}
      className="efiling-create-modal"
    >
      <div className="efiling-modal-shell">
        <div className="efiling-modal-header-row">
          <h3 className="efiling-modal-title">Edit E-filing</h3>
          <IconButton
            aria-label="close"
            className="efiling-modal-close-btn"
            onClick={() => !saving && onClose()}
            disabled={saving}
          >
            <CloseIcon />
          </IconButton>
        </div>
        <p className="efiling-modal-category-line">
          Type:{" "}
          <strong>{isBusiness ? "Business" : "Individual"}</strong>
        </p>

        {isBusiness ? (
          <BusinessEfilingFormFields
            form={form}
            setField={setField}
            formErrors={formErrors}
            saving={saving}
            invoiceDropdown={invoiceDropdown}
            onInvoiceSelect={onInvoiceSelect}
            clientId={clientId}
            documentCategory={categoryTab}
            hideStatusAndFilerComment={hideStatusAndFilerComment}
            canEditFilerComment={canEditFilerComment}
          />
        ) : (
          <Grid container spacing={2} className="efiling-form-grid">
            <Grid item xs={12} md={6}>
              <Labelbox
                type="datepicker"
                labelname="Date"
                value={form.filing_date || null}
                changeData={(v) => setField("filing_date", v || "")}
                required
                error={Boolean(formErrors.filing_date)}
                errmsg={formErrors.filing_date}
                disabled={saving}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Labelbox
                type="select"
                labelname="INV No."
                dropdown={invoiceDropdown}
                value={form.invoice_no === "" ? "" : String(form.invoice_no)}
                changeData={(v) => {
                  setField("invoice_no", v);
                  if (onInvoiceSelect) onInvoiceSelect(v);
                }}
                required
                error={Boolean(formErrors.invoice_no)}
                errmsg={formErrors.invoice_no}
                disabled={saving}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <ReadonlyField
                label="Last name"
                value={clientLast}
                error={Boolean(formErrors.client_last)}
                errmsg={formErrors.client_last}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <ReadonlyField
                label="First name"
                value={clientFirst}
                error={Boolean(formErrors.client_first)}
                errmsg={formErrors.client_first}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Labelbox
                type="text"
                labelname="Fed Due/Ref"
                value={form.fed_due_ref}
                changeData={(v) => setField("fed_due_ref", v)}
                required
                error={Boolean(formErrors.fed_due_ref)}
                errmsg={formErrors.fed_due_ref}
                disabled={saving}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Labelbox
                type="select"
                labelname="F Payment Method"
                dropdown={PAYMENT_METHOD_OPTIONS}
                value={form.fed_payment_method || ""}
                changeData={(v) => setField("fed_payment_method", v)}
                required
                error={Boolean(formErrors.fed_payment_method)}
                errmsg={formErrors.fed_payment_method}
                disabled={saving}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Labelbox
                type="text"
                labelname="State Due/Ref"
                value={form.state_due_ref}
                changeData={(v) => setField("state_due_ref", v)}
                required
                error={Boolean(formErrors.state_due_ref)}
                errmsg={formErrors.state_due_ref}
                disabled={saving}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Labelbox
                type="select"
                labelname="S Payment Method"
                dropdown={PAYMENT_METHOD_OPTIONS}
                value={form.state_payment_method || ""}
                changeData={(v) => setField("state_payment_method", v)}
                required
                error={Boolean(formErrors.state_payment_method)}
                errmsg={formErrors.state_payment_method}
                disabled={saving}
              />
            </Grid>

            {!hideStatusAndFilerComment ? (
              <Grid item xs={12} md={6}>
                <Labelbox
                  type="select"
                  labelname="Status"
                  dropdown={FILING_STATUS_OPTIONS}
                  value={form.filing_status || "Pending"}
                  changeData={(v) => setField("filing_status", v)}
                  disabled={saving}
                  error={Boolean(formErrors.filing_status)}
                  errmsg={formErrors.filing_status}
                />
              </Grid>
            ) : null}
            <Grid item xs={12} md={6}>
              <Labelbox
                type="text"
                labelname="Preparer comment"
                value={form.filer_comment}
                changeData={(v) => setField("filer_comment", v)}
                required
                error={Boolean(formErrors.filer_comment)}
                errmsg={formErrors.filer_comment}
                disabled={saving}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Labelbox
                type="select"
                labelname="Client Mgr Verified"
                dropdown={CLIENT_MGR_VERIFIED_OPTIONS}
                value={form.client_mgr_verified || ""}
                changeData={(v) => setField("client_mgr_verified", v)}
                required
                error={Boolean(formErrors.client_mgr_verified)}
                errmsg={formErrors.client_mgr_verified}
                disabled={saving}
              />
            </Grid>

            {!hideStatusAndFilerComment && canEditFilerComment ? (
              <Grid item xs={12} md={6}>
                <Labelbox
                  type="text"
                  labelname="Filer comment"
                  value={form.other_comments}
                  changeData={(v) => setField("other_comments", v)}
                  error={Boolean(formErrors.other_comments)}
                  errmsg={formErrors.other_comments}
                  disabled={saving}
                />
              </Grid>
            ) : !hideStatusAndFilerComment ? (
              <Grid item xs={12} md={6}>
                <ReadonlyField
                  label="Filer comment"
                  value={form.other_comments}
                />
              </Grid>
            ) : null}

            <Grid item xs={12} md={6}>
              <Labelbox
                type="text"
                labelname="Ack. status"
                value={form.ack_status}
                changeData={(v) => setField("ack_status", v)}
                required
                error={Boolean(formErrors.ack_status)}
                errmsg={formErrors.ack_status}
                disabled={saving}
              />
            </Grid>
            <Grid item xs={12}>
              <EfilingConsentField
                clientId={clientId}
                value={form.signed_consents_link}
                onChange={(v) => setField("signed_consents_link", v)}
                disabled={saving}
                documentCategory={categoryTab}
              />
            </Grid>
          </Grid>
        )}

        <div className="efiling-modal-actions">
          <Button
            text="Cancel"
            ui="transparent"
            variant="outline"
            disable={saving}
            handleClick={onClose}
          />
          <Button
            text={saving ? "Saving…" : "Save"}
            disable={saving}
            handleClick={onSave}
          />
        </div>
      </div>
    </Modal>
  );
}
